import React, { useState } from 'react';
import { Button } from '@/shared/infra/ui/components/ui/button';
import { DASHBOARD_TEXT } from '@/features/dashboard/infra/ui/constants/dashboardText';
import { useNavigate } from 'react-router-dom';
import useLogOut from '@/shared/infra/hooks/useLogout';
import { useAuth } from '@/shared/infra/hooks/useAuth';
import { Resource, Action, createPermission, usePermission, type Permission } from '@/shared/auth';

export interface DashboardSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

interface NavItem {
  label: string;
  icon: string;
  children: {
    label: string;
    path: string;
  }[];
  /** A leaf entry: opens this path itself, with no sublinks (the Visa Matrix). */
  path?: string;
}

// Permission required to open each sidebar path — must mirror the route guards in AppRouter.tsx.
// Partial: paths without an entry are un-gated by design (lookup yields undefined).
// Exported for the mirror-invariant test.
export const ROUTE_PERMISSIONS: Partial<Record<string, Permission>> = {
  '/active-i9-queue': createPermission(Resource.DASHBOARD, Action.READ),
  // Client-facing + internal — gated on formI9Storage, which client users hold only via a
  // per-user custom permission set (VTK-1878 interim model; never a client role default).
  '/storage': createPermission(Resource.FORM_I9_STORAGE, Action.READ),
  // '/digitization' is deliberately ABSENT — the digitized-record list is auth-only. The paid
  // permission gates the act of digitizing (/digitization/batch), not reading the results.
  // Client-facing remediation queue (VTK-1865/1742) — mirrors the /remediation route guard.
  '/remediation': createPermission(Resource.FORM_I9_REMEDIATION, Action.READ),
  '/employees/invite': createPermission(Resource.SECTION_1, Action.READ),
  '/all-everify-cases': createPermission(Resource.EVERIFY, Action.READ),
  // Super-admin only as well (SUPER_ADMIN_ROUTES); the permission mirrors the route.
  '/admin/e-verify-queue': createPermission(Resource.EVERIFY, Action.READ),
  // The internal authoring workspace: super-admin AND the digitization write permission (see route).
  '/digitization/batch': createPermission(Resource.FORM_I9_DIGITIZATION, Action.UPDATE),
};

// Paths restricted to internal VT super admins — must mirror requireSuperAdmin in AppRouter.tsx.
// Storage is now permission-gated (above); the internal upload/split/extract workspace, the
// Visa Matrix page (its one sidebar entry) and the cross-account E-Verify queue stay here.
export const SUPER_ADMIN_ROUTES = new Set([
  '/digitization/batch',
  DASHBOARD_TEXT.sidebar.visaMatrixPath,
  '/admin/e-verify-queue',
]);

/**
 * Every Visa Matrix route (VTK-2063, VTK-2064), each behind `requireSuperAdmin` in AppRouter.tsx.
 * The sidebar links only the first (the page's tabs are the next three); the inspector is reached
 * from an I-9. Listed here so one spec can hold the router to all of them.
 */
export const VISA_MATRIX_ROUTES = new Set([
  '/visa-matrix/rules',
  '/visa-matrix/compare',
  '/visa-matrix/extension-rules',
  '/visa-matrix/outcomes',
  '/visa-matrix/inspector',
  '/visa-matrix/inspector/:i9FormId',
  '/visa-matrix/document-move',
  '/visa-matrix/document-move/:i9FormId',
  '/visa-matrix/move-runs',
]);

const navItems: NavItem[] = [
  {
    label: DASHBOARD_TEXT.sidebar.i9Queue,
    icon: '/svg/i9queue.svg',
    children: DASHBOARD_TEXT.sidebar.i9QueueChildren,
  },
  {
    label: DASHBOARD_TEXT.sidebar.employees,
    icon: '/svg/employee_menu.svg',
    children: DASHBOARD_TEXT.sidebar.employeesChildren,
  },
  {
    label: DASHBOARD_TEXT.sidebar.reports,
    icon: '/svg/reports_menu.svg',
    children: DASHBOARD_TEXT.sidebar.reportsChildren,
  },
  {
    label: DASHBOARD_TEXT.sidebar.visaMatrix,
    icon: '/svg/review.svg',
    children: [],
    path: DASHBOARD_TEXT.sidebar.visaMatrixPath,
  },
];

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ collapsed }) => {
  // Keyed by label, not index — the rendered list is filtered by permission, so indices shift
  // as permissions resolve and would leave the wrong group expanded.
  const [openMenus, setOpenMenus] = useState<{ [label: string]: boolean }>({});
  const navigate = useNavigate();
  const { handleLogOut } = useLogOut();
  const { hasPermission } = usePermission();
  const { role } = useAuth();
  // Normalized roleKey (VTK-1828), not the display string `vtAppRole` (broker sends `super_admin`).
  const isSuperAdmin = role === 'super_admin';

  // A super-admin-only path can ALSO carry a permission requirement (the route guards check both),
  // so the role check narrows rather than short-circuits.
  const canAccess = (path: string) => {
    if (SUPER_ADMIN_ROUTES.has(path) && !isSuperAdmin) return false;
    const required = ROUTE_PERMISSIONS[path];
    return !required || hasPermission(required);
  };

  // Inaccessible items are HIDDEN, not shown disabled — a user shouldn't be advertised
  // features their account hasn't bought. A group whose every child is hidden drops out too,
  // so no menu expands to nothing. A leaf entry stays by its own path.
  const visibleNavItems = navItems
    .map((item) => ({ ...item, children: item.children.filter((c) => canAccess(c.path)) }))
    .filter(
      (item) => item.children.length > 0 || (item.path !== undefined && canAccess(item.path))
    );

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside
      className={`h-full bg-primary transition-all duration-300 ${collapsed ? 'w-16 items-center' : 'w-64'} flex flex-col text-white`}
      style={{ transitionProperty: 'width,background-color', transitionDuration: '300ms' }}
      aria-label="Sidebar"
    >
      <div className={`p-2 ${collapsed ? 'flex flex-col items-center' : ''}`}>
        <Button
          variant="default"
          size="lg"
          onClick={() => {
            navigate('/dashboard');
          }}
          className={`w-full flex bg-[linear-gradient(90deg,var(--dashboard_button_primary_gradient)_0%,var(--dashboard_button_secondary_gradient)_100%)] items-center mb-2 justify-center ${collapsed ? 'p-2' : 'gap-2'} cursor-pointer transition-all duration-150 active:scale-95 hover:brightness-90`}
        >
          <img
            src="/svg/dashboard_icon.svg"
            alt="dashboard"
            width={20}
            height={20}
            style={{ display: 'inline-block' }}
          />
          {!collapsed && <span className="font-semibold">{DASHBOARD_TEXT.sidebar.dashboard}</span>}
        </Button>
      </div>
      <nav className={`flex-1 px-2 ${collapsed ? 'flex flex-col items-center' : ''}`}>
        <ul className={`space-y-2 ${collapsed ? 'flex flex-col items-center' : ''}`}>
          {visibleNavItems.map((item) => (
            <li key={item.label} className={collapsed ? 'flex flex-col items-center' : ''}>
              <div
                className={`flex items-center gap-2 cursor-pointer py-2 px-2 rounded hover:bg-primary/80 ${collapsed ? 'justify-center' : ''}`}
                onClick={() => (item.path ? navigate(item.path) : toggleMenu(item.label))}
                {...(item.path
                  ? {
                      role: 'link',
                      tabIndex: 0,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(item.path!);
                        }
                      },
                    }
                  : {})}
              >
                <img src={item.icon} alt={item.label} width={18} height={18} />
                <span
                  className="font-semibold"
                  style={{
                    transition: 'opacity 0.3s, transform 0.3s',
                    opacity: collapsed ? 0 : 1,
                    transform: collapsed ? 'translateX(-20px)' : 'translateX(0)',
                    pointerEvents: collapsed ? 'none' : 'auto',
                    display: collapsed ? 'none' : 'inline',
                  }}
                >
                  {item.label}
                </span>
                {!collapsed && !item.path && (
                  <span className="ml-auto">
                    <img
                      src="/svg/arrow_up.svg"
                      alt={openMenus[item.label] ? 'Collapse' : 'Expand'}
                      width={16}
                      height={16}
                      style={{
                        transform: openMenus[item.label] ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </span>
                )}
              </div>
              {!collapsed && openMenus[item.label] && (
                <ul className="pl-8 mt-2 space-y-1 text-left">
                  {item.children.map((child) => (
                    <li
                      key={child.label}
                      className="text-sm font-medium text-left hover:underline cursor-pointer"
                      onClick={() => navigate(child.path)}
                    >
                      {child.label}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <div className={`mt-8 space-y-2 ${collapsed ? 'flex flex-col items-center' : ''}`}>
          <div
            className={`flex items-center font-semibold gap-2 mt-2 px-2 rounded hover:bg-primary/80 cursor-pointer ${collapsed ? 'justify-center' : ''}`}
            onClick={handleLogOut}
          >
            <img src="/svg/logout.svg" alt={DASHBOARD_TEXT.sidebar.logout} width={18} height={18} />
            {!collapsed && <span>{DASHBOARD_TEXT.sidebar.logout}</span>}
          </div>
        </div>
      </nav>
    </aside>
  );
};
